import { bindable, customElement, resolve } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import template from './person-modal.html';
import type { Person } from '../services/api.types';
import { createPerson, updatePerson } from '../services/api.service';

@customElement({ name: 'person-modal', template })
export class PersonModal {
  private readonly i18n: I18N = resolve(I18N);

  @bindable public saved!: (person: Person) => void;
  @bindable public closed!: () => void;
  /** When provided the modal operates in edit mode */
  @bindable public personToEdit: Person | null = null;
  /** Optional prefill for quick-create flows (e.g. from the lightbox marker picker) */
  @bindable public prefillName = '';

  public firstName = '';
  public lastName = '';
  public nickName = '';
  public birthYear: number | null = null;
  public birthMonth: number | null = null;
  public birthDay: number | null = null;
  public saving = false;
  public error = '';

  private _keyHandler!: (e: KeyboardEvent) => void;

  public get isEditMode(): boolean {
    return this.personToEdit !== null;
  }

  public attached(): void {
    // Always reset first: this component's view/view-model instance can be
    // cached and reactivated (rather than recreated) when toggled via `if.bind`,
    // so previous edit/create values must be explicitly cleared here on every
    // activation instead of relying on constructor-time defaults.
    this.firstName = '';
    this.lastName = '';
    this.nickName = '';
    this.birthYear = null;
    this.birthMonth = null;
    this.birthDay = null;
    this.error = '';
    this.saving = false;

    if (this.personToEdit) {
      this.firstName = this.personToEdit.firstName;
      this.lastName = this.personToEdit.lastName;
      this.nickName = this.personToEdit.nickName ?? '';
      this.birthYear = this.personToEdit.birthYear ?? null;
      this.birthMonth = this.personToEdit.birthMonth ?? null;
      this.birthDay = this.personToEdit.birthDay ?? null;
    } else if (this.prefillName) {
      const parts = this.prefillName.trim().split(/\s+/);
      this.firstName = parts[0] ?? '';
      this.lastName = parts.slice(1).join(' ');
    }
    this._keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') this.close();
    };
    document.addEventListener('keydown', this._keyHandler);
  }

  public detached(): void {
    document.removeEventListener('keydown', this._keyHandler);
  }

  public close(): void {
    this.closed();
  }

  public clearBirthday(): void {
    this.birthYear = null;
    this.birthMonth = null;
    this.birthDay = null;
  }

  public async save(): Promise<void> {
    if (!this.firstName.trim() && !this.lastName.trim() && !this.nickName.trim()) {
      this.error = this.i18n.tr('persons.form.nameRequiredError');
      return;
    }
    this.saving = true;
    this.error = '';
    try {
      const payload = {
        firstName: this.firstName.trim(),
        lastName: this.lastName.trim(),
        nickName: this.nickName.trim() || null,
        birthYear: this.birthYear,
        birthMonth: this.birthMonth,
        birthDay: this.birthDay,
      };
      const person = this.isEditMode && this.personToEdit
        ? await updatePerson(this.personToEdit.id, { ...payload, clearBirthDate: this.birthYear == null && this.birthMonth == null && this.birthDay == null })
        : await createPerson(payload);
      this.saved(person);
    } catch (e: unknown) {
      this.error = e instanceof Error ? e.message : String(e);
    } finally {
      this.saving = false;
    }
  }
}
